import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import JsonLd from '@/components/feature/JsonLd';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { dramas } from '@/mocks/dramas';
import { weeklyCalendar, todayWeekday } from '@/mocks/calendar';
import type { CalendarDay, Drama } from '@/types/content';

const allDramas = dramas as Drama[];
const allOption = '全部短剧';

interface TrackingRow {
  weekdayLabel: string;
  dramaId: string;
  dramaTitle: string;
  range: string;
  time: string;
}

const trackingRows: TrackingRow[] = (weeklyCalendar as CalendarDay[]).flatMap((day) =>
  day.items.map((item) => ({
    weekdayLabel: day.label,
    dramaId: item.dramaId,
    dramaTitle: item.dramaTitle,
    range: item.range,
    time: item.time,
  })),
);

export default function CalendarPage() {
  const [selected, setSelected] = useState<string>(allOption);

  useDocumentMeta({
    title: '追剧日历｜本周短剧更新时间安排与连续剧集更新顺序 - 爆料短剧',
    description:
      '爆料短剧追剧日历按周整理每日短剧更新时间与集数区间，清晰标注连续剧集更新顺序，哪天更新哪部、更到第几集一眼看清，追剧不漏更。',
    keywords: '追剧日历,短剧更新时间,连续剧集更新顺序,短剧更新表,下一集预告',
    path: '/calendar',
    ogType: 'website',
  });

  const visibleDays = useMemo(() => {
    if (selected === allOption) return weeklyCalendar as CalendarDay[];
    return (weeklyCalendar as CalendarDay[])
      .map((day) => ({ ...day, items: day.items.filter((item) => item.dramaId === selected) }))
      .filter((day) => day.items.length > 0);
  }, [selected]);

  const today = (weeklyCalendar as CalendarDay[]).find((day) => day.weekday === todayWeekday)!;
  const todayVisible = selected === allOption ? today.items : today.items.filter((i) => i.dramaId === selected);

  const calendarSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '追剧日历 · 爆料短剧',
    description: '按周整理短剧每日更新时间与集数区间，标注连续剧集更新顺序。',
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      name: '本周短剧更新安排',
      itemListElement: trackingRows.map((row, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `${row.weekdayLabel} ${row.dramaTitle} ${row.range}`,
      })),
    },
  };

  return (
    <SiteLayout>
      <JsonLd data={calendarSchema} />

      <section className="w-full border-b border-background-200 bg-background-100 py-8 md:py-12">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <Breadcrumbs items={[{ label: '追剧日历' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            追剧日历 · 每周更新一目了然
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600 md:text-base">
            下面是本周的短剧更新时间安排。每一天更新哪几部、更新到第几集、几点上线都标得清清楚楚，
            并对齐了每部剧的连续剧集更新顺序。想看某部剧的专属日历，用右边的筛选切换即可。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="inline-flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-timer-flash-line text-lg" aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs text-primary-700">今天 · {today.label}（2026-09-10）</p>
                <p className="text-sm text-foreground-900">
                  共 {todayVisible.length} 部短剧更新
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="drama-filter" className="whitespace-nowrap text-sm text-foreground-600">
                按短剧筛选
              </label>
              <select
                id="drama-filter"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
              >
                <option value={allOption}>{allOption}</option>
                {allDramas
                  .filter((drama) => drama.status === '更新中')
                  .map((drama) => (
                    <option key={drama.id} value={drama.id}>
                      {drama.title}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {selected === allOption && (
            <div className="mt-6 rounded-lg border border-dashed border-accent-300 bg-accent-50/70 p-4 md:p-5">
              <p className="flex items-center gap-2 text-sm text-accent-900">
                <i className="ri-calendar-check-line" aria-hidden="true" />
                今晚重点更新
              </p>
              <ul className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-4">
                {today.items.map((item) => (
                  <li key={item.dramaId} className="flex items-center gap-2 text-sm text-foreground-700">
                    <span className="font-display text-accent-700">{item.time}</span>
                    <Link
                      to={`/drama/${item.dramaId}`}
                      className="cursor-pointer transition-colors hover:text-primary-600"
                    >
                      《{item.dramaTitle}》{item.range}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            {visibleDays.map((day) => {
              const isToday = day.weekday === todayWeekday;
              return (
                <div
                  key={day.weekday}
                  className={`flex flex-col rounded-lg border p-4 ${
                    isToday ? 'border-primary-400 bg-background-50' : 'border-background-200 bg-background-50/70'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-heading text-sm font-bold ${isToday ? 'text-primary-600' : 'text-foreground-900'}`}>
                      {day.label}
                    </span>
                    {isToday && (
                      <span className="rounded-md bg-primary-500 px-1.5 py-0.5 text-[10px] text-background-50">今天</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-foreground-400">{day.date}</p>
                  <ul className="mt-3 flex-1 space-y-2">
                    {day.items.map((item) => (
                      <li key={`${day.weekday}-${item.dramaId}`}>
                        <Link
                          to={`/drama/${item.dramaId}`}
                          className="block cursor-pointer rounded-md bg-background-100 px-2.5 py-2 transition-colors hover:bg-primary-50"
                        >
                          <span className="block text-xs text-foreground-900">{item.dramaTitle}</span>
                          <span className="mt-1 flex items-center justify-between text-[11px] text-foreground-500">
                            <span>{item.range}</span>
                            <span className="text-accent-700">{item.time}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 border-t border-background-200 pt-2 text-[11px] text-foreground-400">
                    {day.items.length} 部更新
                  </p>
                </div>
              );
            })}

            {visibleDays.length === 0 && (
              <div className="col-span-full rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <i className="ri-calendar-close-line text-4xl text-background-400" aria-hidden="true" />
                <p className="mt-4 text-sm text-foreground-700">该剧本周没有排入更新</p>
                <p className="mt-1 text-xs text-foreground-500">可能是已完结或处于停更期，切换其他短剧看看</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">更新追踪表</h2>
          <p className="mt-3 text-sm text-foreground-600">
            把日历整理成一张表格，方便对照剧名、更新日与集数区间。发现平台调整档期时，我们会同步修订这张表。
          </p>

          <div className="mt-6 overflow-x-auto rounded-lg border border-background-200">
            <table className="w-full min-w-[720px] border-collapse bg-background-50 text-left text-sm">
              <thead>
                <tr className="bg-secondary-900 text-background-50">
                  <th scope="col" className="px-4 py-3 font-medium">更新日</th>
                  <th scope="col" className="px-4 py-3 font-medium">剧名</th>
                  <th scope="col" className="px-4 py-3 font-medium">集数区间</th>
                  <th scope="col" className="px-4 py-3 font-medium">上线时间</th>
                  <th scope="col" className="px-4 py-3 font-medium">剧情介绍</th>
                </tr>
              </thead>
              <tbody>
                {trackingRows
                  .filter((row) => selected === allOption || row.dramaId === selected)
                  .map((row) => (
                    <tr key={`${row.weekdayLabel}-${row.dramaId}-${row.range}`} className="border-b border-background-200 last:border-b-0">
                      <td className="px-4 py-3 text-foreground-700">{row.weekdayLabel}</td>
                      <td className="px-4 py-3 text-foreground-900">{row.dramaTitle}</td>
                      <td className="px-4 py-3 text-foreground-700">{row.range}</td>
                      <td className="px-4 py-3">
                        <span className="font-display text-accent-700">{row.time}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/drama/${row.dramaId}`}
                          className="cursor-pointer text-primary-600 transition-colors hover:text-primary-700"
                        >
                          查看分集介绍
                        </Link>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 flex items-start gap-2 text-xs leading-6 text-foreground-500">
            <i className="ri-information-line mt-0.5 text-foreground-400" aria-hidden="true" />
            日历以各平台官方公布的更新节奏为准，如遇加更、停更或档期调整，会在对应剧集页面标注最新安排。
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}